import {
  isInvalidVendorName,
  isInvalidEmail,
  isInvalidString,
  isInvalidUserName,
  isInvalidRole
} from "src/validation/stringValidation";
import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import { UpdateUserAccountFormDataKey } from "src/types";

class UpdateUserFormDataValidator extends FormValidator {
  private static validateEmail(value: unknown): ValidationError<"email"> | undefined {
    return isInvalidEmail(value) ? new ValidationError("Enter a valid email address", "email") : undefined;
  }

  private static validateUserName(value: unknown): ValidationError<"name"> | undefined {
    return value && isInvalidUserName(value) ? new ValidationError("Enter a valid user name", "name") : undefined;
  }

  private static validateVendorNames(value: unknown): ValidationError<"vendorNames"> | undefined {
    if (!Array.isArray(value) || !value.length) {
      return new ValidationError("Invalid vendor names", "vendorNames");
    }

    for (const arrayValue of value) {
      if (isInvalidVendorName(arrayValue)) {
        return new ValidationError("Enter a valid vendor name", "vendorNames");
      }
    }

    return undefined;
  }

  private static validateJobTitle(value: unknown): ValidationError<"jobTitle"> | undefined {
    return value && isInvalidString(value, 1, 20)
      ? new ValidationError("Enter a valid job title", "jobTitle")
      : undefined;
  }

  private static validateSupervisorEmails(value: unknown): ValidationError<"supervisorEmails"> | undefined {
    if (!Array.isArray(value) || !value.length || (value.length === 1 && value[0] === "")) {
      return;
    }

    for (const arrayValue of value) {
      if (isInvalidEmail(arrayValue)) {
        return new ValidationError("Invalid supervisor email(s)", "supervisorEmails");
      }
    }

    return undefined;
  }

  private static validateRole(value: unknown): ValidationError<"role"> | undefined {
    return isInvalidRole(value) ? new ValidationError("Enter a valid role", "role") : undefined;
  }

  private static readonly validationStrategies: Record<
    UpdateUserAccountFormDataKey,
    (value: unknown) => ValidationError<UpdateUserAccountFormDataKey> | undefined
  > = {
    email: UpdateUserFormDataValidator.validateEmail,
    name: UpdateUserFormDataValidator.validateUserName,
    vendorNames: UpdateUserFormDataValidator.validateVendorNames,
    jobTitle: UpdateUserFormDataValidator.validateJobTitle,
    supervisorEmails: UpdateUserFormDataValidator.validateSupervisorEmails,
    role: UpdateUserFormDataValidator.validateRole
  };

  static validateUserFormData<T extends UpdateUserAccountFormDataKey>(
    formData: Partial<Record<T, unknown>>
  ): ValidationErrors<T> {
    return super.validateFormData(formData, UpdateUserFormDataValidator.validationStrategies) as ValidationErrors<T>;
  }
}

export { UpdateUserFormDataValidator };
